import React, { CSSProperties, useEffect } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { appDispatch, IUsers, RootState } from 'store/store';
import UserSelector from '../../../../../UserSelector';
import {
  IRoleOption,
  IUserOption,
} from '../../../../../UserSelector/UserSelector';
import { Label, Wrapper } from './style';
import Icon from '../../../../../Icon/Icon';
import { ICompany, IInvitedMember } from 'store/companies/companiesTypes';
import { batchAddMembersToCompany } from '../../../../../../store/companies/companiesSlice';

interface IStateToProps {
  users: IUsers['map'];
}
type IOwnProps = {
  companyMembers: ICompany['members'];
  roleIds?: number[] | undefined;
  userIds?: number[] | undefined;
  onSelectUser: (userIds: number[]) => void;
  label: string;
  placeholder: string;
  selected?: number[];
  multiSelect?: boolean;
  requireSeveralUser: (usersList: number[]) => Promise<void>;
  showRoles?: boolean;
  company?: ICompany;
  allowAdd?: boolean;
  wrapperStyle?: CSSProperties | undefined;
  usePop?: boolean;
  maxWidth?: string;
};
type IComponentProps = IOwnProps & IStateToProps;
const CompanyMemberSelect: React.FC<IComponentProps> = ({
  users,
  companyMembers,
  onSelectUser,
  label,
  selected,
  placeholder,
  roleIds,
  userIds,
  requireSeveralUser,
  multiSelect,
  showRoles,
  company,
  allowAdd,
  wrapperStyle,
  usePop = true,
  maxWidth = '800px',
}) => {
  const useFetchingUsers = (
    userList: number[],
    requireSeveralUser: (usersList: number[]) => Promise<void>
  ) => {
    useEffect(() => {
      if (userList.length > 0) requireSeveralUser(userList);
    }, [userList, requireSeveralUser]);
  };

  const handleChangeValue = (values: Array<IUserOption | IRoleOption>) => {
    if (values.length === 0) onSelectUser([]);
    else onSelectUser(values.map((it) => Number((it as IUserOption).userId)));
  };
  const toFetchIds = [] as number[];
  const foundRoles = new Set<number>();
  const companyUsers = Object.values(companyMembers)
    .filter((member) => {
      return (
        member !== undefined &&
        (!userIds || userIds.includes(member.userId)) &&
        (!roleIds ||
          roleIds.length === 0 ||
          Object.keys(member.roles).filter((value) =>
            roleIds.includes(Number(value))
          ).length > 0)
      );
    })
    .reduce(
      (usersList, member) => {
        if (!member) return usersList;
        if (!users[member.userId]) toFetchIds.push(member.userId);
        Object.keys(member.roles).forEach((it) => foundRoles.add(Number(it)));
        usersList!.push({
          userId: member.userId,
          name: users[member.userId]?.name || member.userId.toString(),
          roles: Object.values(member.roles),
          optionType: 'user',
        });
        return usersList;
      },
      [] as Array<IUserOption | IRoleOption>
    );

  if (showRoles && company) {
    Array.from(foundRoles).forEach((roleId) => {
      companyUsers.push({
        roleId: roleId,
        name: company.roles[roleId].name,
        optionType: 'role',
      } as IRoleOption);
    });
  }
  useFetchingUsers(toFetchIds, requireSeveralUser);
  if (toFetchIds.length)
    return (
      <div className="progress">
        <div className="indeterminate" />
      </div>
    );
  return (
    <Wrapper style={wrapperStyle}>
      <Icon type={multiSelect ? 'users' : 'user'} style={{ margin: '5px' }} />
      <Label>{label + ':  '}</Label>
      <UserSelector
        usePop={usePop !== false}
        label={label}
        onChangeValues={handleChangeValue}
        values={
          selected?.map((selectedUserId) => {
            return {
              userId: selectedUserId,
              name: users[selectedUserId].name || selectedUserId.toString(),
              roles: Object.values(companyMembers[selectedUserId]?.roles || {}),
              optionType: 'user',
            };
          }) ?? []
        }
        options={Object.values(companyUsers)}
        canChangeProgress={false}
        singleChoice={!multiSelect}
        allowAdd={allowAdd}
        companyId={company?.id}
        addMultipleMembersToCompany={(
          invitedMembers: IInvitedMember[],
          forceRename: boolean,
          companyId: number
        ) =>
          appDispatch(
            batchAddMembersToCompany({ invitedMembers, forceRename, companyId })
          )
        }
        type={'noAssignment'}
        emphasizeSelect={placeholder}
        largeScreen={true}
        maxWidth={maxWidth}
      />
    </Wrapper>
  );
};
const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state
) => {
  const users = state.users.map;
  return { users };
};
export default connect(mapStateToProps, null)(CompanyMemberSelect as any);
